import type {
  BankBreakMode, CabinetState, HelpCardLine, MachineId, SimEvent, Tag, TokenSpec,
} from '../../shared/contract.js';
import type { Economy } from '../economy.js';
import { ctxFor, type EscapeMode, type Pipeline, type ScoreInfo, type SimApi } from '../modifier.js';
import type { PhysicsWorld } from '../physics/world.js';
import type { RngStreams } from '../rng.js';
import { toState, type TokenRuntime } from '../token.js';
import { add, type Vec3 } from '../vec.js';

/**
 * Cabinet base: base rules + the four universal verbs + a tag interpretation table +
 * a structural limit + a Hausregeln board. machines.md: "a cabinet that can't state all
 * five doesn't ship", so all five live here and every subclass must fill them in.
 *
 * THE COVENANT: nothing below this line, and nothing in any subclass, may name an item.
 * Machines interpret *tags*, through their declared table, which is data. The covenant
 * lint (test/covenant-lint.ts) fails the build on any item id appearing in core/machines.
 */

/** A declared odds mapping — the machine's only license to interpret a modifier. */
export interface TagRule {
  /** effect key the machine's own code switches on (never an item id) */
  effect: string;
  /** printed on the machine's help card */
  note: string;
}

export type TagTable = Partial<Record<Tag, TagRule>>;

/** Analytic field geometry, used by the [Geist] overlap probe (exact, engine-free). */
export type FieldSolid =
  | { kind: 'ball'; pos: Vec3; r: number }
  | { kind: 'box'; pos: Vec3; h: Vec3 };

export interface CabinetHost {
  readonly tick: number;
  readonly dt: number;
  readonly activeMachine: MachineId | null;
  readonly pipe: Pipeline;
  readonly phys: PhysicsWorld;
  readonly rng: RngStreams;
  readonly economy: Economy;
  readonly api: SimApi;
  emit(e: SimEvent): void;
  /** `physical: false` makes an abstract token (a BANDIT reel symbol) with no body */
  spawnToken(spec: TokenSpec, machine: MachineId, pos: Vec3, vel: Vec3, physical?: boolean): TokenRuntime;
  removeToken(id: number, reason: 'scored' | 'lost' | 'transfer'): void;
  tokensIn(machine: MachineId): TokenRuntime[];
  /** run the full payout pipeline for a scoring event */
  award(info: ScoreInfo, cabinet: Cabinet): { muenzen: number; tickets: number };
  /** a labelled, GLÜCK-bendable, [Fluch]-invertible roll */
  roll(stream: string, event: string, p: number, token: TokenRuntime | null): boolean;
  /** draw a token spec from Der Beutel (deck = reels) */
  drawFromBeutel(stream: string): TokenSpec;
  /** a token left the cabinet; the sim decides lose-vs-room */
  escaped(token: TokenRuntime, from: Cabinet, mode: EscapeMode, at: Vec3): void;
}

export const TILT_PER_SHOVE = 0.22;
export const TILT_DECAY_PER_SEC = 0.06;
export const TILT_LOCKOUT_SECONDS = 4;
export const STRAEHNE_MAX = 5;
/** Value a floor-1 cabinet is built to hold before its structure complains. */
export const VALUE_CAP_BASE = 4000;

export abstract class Cabinet<S = unknown> {
  abstract readonly id: MachineId;
  /** the machine's declared odds mapping; DATA, not code */
  abstract readonly tagTable: TagTable;
  /** what bursts this cabinet, in words, for the help card */
  abstract readonly limitLabel: string;
  /** contract v1: what physically breaks when the bank goes (machines.md) */
  abstract readonly breakMode: BankBreakMode;

  /** pot capacity — a balance number, so it lives on the sim side of the waist */
  topfMax = 400;

  /** cabinets travel with you, but the House chooses which are switched on each floor */
  origin: Vec3;
  protected readonly host: CabinetHost;

  topf = 0;
  straehne = 1;
  tilt01 = 0;
  limit01 = 0;
  broken = false;
  /** the House can leave a cabinet dark this floor — build denial as soft difficulty */
  powered = true;
  /** Nachlegen escalation level: 1 = base stakes */
  stakeLevel = 1;
  /** re-entries this run; each raises the buy-in (route pressure, core-loop.md) */
  entries = 0;
  lockoutUntil = -1;
  /** structural load is measured over a window so a burst is a burst, not a spike */
  protected loadWindow: number[] = [];

  constructor(host: CabinetHost, origin: Vec3) {
    this.host = host;
    this.origin = origin;
  }

  /** world position of a cabinet-local point */
  protected w(local: Vec3): Vec3 {
    return add(this.origin, local);
  }

  /** is the player zoomed into this cabinet right now? */
  protected get active(): boolean {
    return this.host.activeMachine === this.id;
  }

  /**
   * Where a token arriving from the room lands (Der Deckel). All cabinets share one
   * world coordinate system from day one, exactly so this is a translation and not a
   * special case (architecture.md).
   */
  intakePoint(): Vec3 {
    return this.w({ x: 0, y: 3, z: 0 });
  }

  // ---------------------------------------------------------------- subclass duties
  abstract build(): void;
  abstract fieldSolids(): readonly FieldSolid[];
  abstract setzen(p: S): void;
  abstract tickMachine(dt: number): void;
  abstract extra(): unknown;
  /** 0..1 structural load, before modifier scaling */
  abstract structuralLoad(): number;
  /** value that pours out when the cabinet bursts */
  abstract burstValue(): number;

  // ---------------------------------------------------------------- the four verbs

  /** Verb 2: Rütteln — shove the cabinet. The classic sin; VERDACHT's favourite. */
  ruetteln(strength = 1): boolean {
    if (this.broken) return false;
    if (this.host.tick < this.lockoutUntil) return false;
    // Die Kamera halves the room you have to shove in. It is a query, not a special case.
    const scale = this.host.pipe.query('tiltScale', ctxFor(this.id, this.host.tick, null), 1);
    const amount = TILT_PER_SHOVE * strength * scale;
    this.tilt01 = Math.min(1, this.tilt01 + amount);
    this.host.emit({ t: 'tilt', machine: this.id, amount });

    for (const t of this.host.tokensIn(this.id)) {
      if (t.body < 0 || t.stuck) continue;
      this.host.phys.wake(t.body);
      // A shove has to lift a token clear of a tray lip, or a parked tray is a trap
      // rather than a decision (and the TILT meter is what prices it). The lift is the
      // point; lateral spread is kept small on purpose, because a shove that flings
      // fifty coins off the sides of the pusher makes Rütteln strictly bad there
      // instead of the tempting sin it is meant to be.
      this.host.phys.impulse(t.body, {
        x: (this.host.rng.float('ruetteln') - 0.5) * 1.1 * strength,
        y: 1.5 * strength,
        z: (this.host.rng.float('ruetteln') - 0.5) * 1.1 * strength,
      });
    }
    this.onShove(strength);

    if (this.tilt01 >= 1) {
      this.lockoutUntil = this.host.tick + Math.round(TILT_LOCKOUT_SECONDS / this.host.dt);
      // contract v1: say when it releases, so the TILT bubble can animate toward release
      // instead of sitting red for an unknown duration.
      this.host.emit({ t: 'tiltLockout', machine: this.id, until: this.lockoutUntil });
      this.host.economy.addVerdacht(6, 'Rüttel-Exzess', (e) => this.host.emit(e));
    } else {
      this.host.economy.addVerdacht(1, 'Rütteln', (e) => this.host.emit(e));
    }
    return true;
  }

  /** machine-specific reaction to a shove (pusher stutter, reel jog, …) */
  protected onShove(_strength: number): void {}

  /** Verb 3: GLÜCK — bend one declared upcoming random event. */
  glueckSpend(stream: string, event: string): boolean {
    if (this.broken) return false;
    if (!this.host.economy.spendGlueck(1)) return false;
    this.host.emit({ t: 'glueckSpent', stream, event });
    this.declareBend(stream, event);
    return true;
  }

  protected declareBend(stream: string, event: string): void {
    bends(this.host).add(`${stream}|${event}`);
  }

  /**
   * Consume a declared bend directly, for events that are not a probability.
   *
   * `host.roll()` spends a bend as "one reroll of one declared event", which is the right
   * shape for an odds question (a reel stop, a pocket, a wrapped prize). Some declared
   * events are not odds at all: the FLIPPER's ball save is *deterministic* — you either
   * called it before the ball drained or you did not — and running it through `roll()`
   * with p = 0 would make a declared save silently impossible, because rerolling zero is
   * still zero. Same currency, same declaration, honest resolution.
   */
  protected consumeBend(stream: string, event: string): boolean {
    const key = `${stream}|${event}`;
    const pending = bends(this.host);
    if (!pending.has(key)) return false;
    pending.delete(key);
    return true;
  }

  /** Verb 4a: Kassieren — bank it and walk. */
  kassieren(): { muenzen: number; tickets: number } {
    const out = this.collectRemaining();
    this.host.emit({ t: 'cashOut', machine: this.id, muenzen: out.muenzen, tickets: out.tickets });
    this.host.pipe.fire(
      'onCashOut',
      { ...ctxFor(this.id, this.host.tick, null), api: this.host.api },
      out,
    );
    this.straehne = 1;
    return out;
  }

  /** Verb 4b: Nachlegen — raise the stake, raise the rules. */
  nachlegen(): void {
    if (this.broken) return;
    this.stakeLevel++;
    this.escalate(this.stakeLevel);
    this.host.emit({ t: 'nachlegen', machine: this.id, newStakes: this.stakeLevel });
  }

  /** machine-specific escalation: golden pegs + bust hole, faster pusher, 4th reel… */
  protected abstract escalate(level: number): void;

  /** what the player still has on the table when they cash out */
  protected collectRemaining(): { muenzen: number; tickets: number } {
    return { muenzen: 0, tickets: 0 };
  }

  /** entering (or re-entering) this cabinet costs more each time */
  buyIn(): number {
    return Math.round(5 * this.stakeLevel * Math.pow(1.6, this.entries));
  }

  // ---------------------------------------------------------------- shared mechanics

  /** A scoring event: runs the payout pipeline, moves STRÄHNE, loads the structure. */
  protected registerScore(token: TokenRuntime, pocket: string, value: number, at: Vec3, jackpot: boolean): void {
    const info: ScoreInfo = { token: token.id, pocket, value, at, jackpot };
    this.host.emit({
      t: 'score', token: token.id, pocket, value, at: [at.x, at.y, at.z], tags: [...token.tags],
    });
    token.lastScore = { pocket, value, at };

    const paid = this.host.award(info, this);
    if (jackpot) {
      this.host.emit({ t: 'jackpot', machine: this.id, value: paid.muenzen + paid.tickets });
      this.topf = 0;
    }
    this.setStraehne(Math.min(STRAEHNE_MAX, this.straehne + 1));
    this.loadWindow.push(this.host.tick);
  }

  /**
   * contract v1: the flame must *ignite* and, on a dud, *snuff* — opposite motions the
   * renderer cannot distinguish by diffing a number across snapshots.
   */
  protected setStraehne(to: number): void {
    const from = this.straehne;
    if (from === to) return;
    this.straehne = to;
    this.host.emit({ t: 'straehne', machine: this.id, from, to });
  }

  /** A dud: breaks the streak and pays the pity engine. */
  protected registerDud(token: TokenRuntime | null): void {
    this.setStraehne(1);
    this.host.economy.registerLoss((e) => this.host.emit(e));
    this.host.pipe.fire(
      'onDud',
      { ...ctxFor(this.id, this.host.tick, token), api: this.host.api },
      { token: token ? token.id : null },
    );
  }

  /** DER TOPF: near-misses are banked, not exploited. */
  protected feedTopf(amount: number): void {
    if (amount <= 0) return;
    this.topf += amount;
    this.host.emit({ t: 'topfFeed', machine: this.id, amount });
  }

  /**
   * How much VALUE this cabinet is holding, against what it was built to hold.
   *
   * machines.md gives each cabinet a physical limit (pile mass, pocket throughput, match
   * value) and those stay. This is the limit they all share, and it is the one that makes
   * grammar law 6 actually true: "broken must be reachable — AND self-limiting". A combo
   * that inflates *value* without inflating mass or throughput — a pile of ripening [Zins]
   * coins is the obvious one — would otherwise run forever, because no per-machine limit
   * is watching the number that is actually exploding. Now it bursts the machine in a
   * shower of glass and coins: huge one-time payout, cabinet out of order, VERDACHT spike.
   * Infinite loops end in fireworks, not in soft-locks.
   */
  protected valueLoad(): number {
    let value = this.topf;
    for (const t of this.host.tokensIn(this.id)) value += t.faceValue;
    // the cap rises with the floor, because floor 6 payouts are legitimately larger
    const cap = VALUE_CAP_BASE * Math.pow(2.1, Math.max(0, this.host.economy.floor - 1));
    return value / cap;
  }

  /** structural limit → Bank sprengen. Infinite loops end in fireworks, not soft-locks. */
  protected checkLimit(): void {
    if (this.broken) return;
    const raw = Math.max(this.structuralLoad(), this.valueLoad());
    const scaled = this.host.pipe.query('limitScale', ctxFor(this.id, this.host.tick, null), raw);
    this.limit01 = Math.max(0, scaled);
    if (this.limit01 < 1) return;

    const payout = Math.round(this.burstValue());
    this.broken = true;
    // contract v1: glass / axle / wheel / tray are three different spectacles and this is
    // the game's money shot. Better decided in the sim than duplicated render-side.
    this.host.emit({ t: 'bankBreak', machine: this.id, payout, mode: this.breakMode });
    this.host.economy.addCoins(payout);
    this.host.economy.addVerdacht(25, 'Bank gesprengt', (e) => this.host.emit(e));
    this.host.pipe.fire(
      'onBankBreak',
      { ...ctxFor(this.id, this.host.tick, null), api: this.host.api },
      { payout },
    );
    this.topf = 0;
  }

  /** Ask the pipeline what leaving this cabinet means (Der Deckel answers 'room'). */
  protected escapeMode(token: TokenRuntime): EscapeMode {
    return this.host.pipe.query('escape', ctxFor(this.id, this.host.tick, token), 'lose');
  }

  tickShared(dt: number): void {
    if (this.tilt01 > 0) this.tilt01 = Math.max(0, this.tilt01 - TILT_DECAY_PER_SEC * dt);
    const cutoff = this.host.tick - Math.round(3 / dt);
    while (this.loadWindow.length > 0 && this.loadWindow[0]! < cutoff) this.loadWindow.shift();
  }

  state(): CabinetState {
    const tokens = this.host.tokensIn(this.id).map((t) => {
      if (t.body < 0) {
        return toState(t, { x: 0, y: 0, z: 0 }, [0, 0, 0, 1], { x: 0, y: 0, z: 0 }, true);
      }
      return toState(
        t,
        this.host.phys.translation(t.body),
        this.host.phys.rotation(t.body),
        this.host.phys.linvel(t.body),
        this.host.phys.isSleeping(t.body),
      );
    });
    return {
      machine: this.id,
      origin: [this.origin.x, this.origin.y, this.origin.z],
      tokens,
      topf: this.topf,
      topfMax: this.topfMax,
      topf01: Math.max(0, Math.min(1, this.topf / this.topfMax)),
      straehne: this.straehne,
      limit01: this.limit01,
      broken: this.broken,
      powered: this.powered,
      hausregeln: this.host.pipe.boardFor(this.id),
      helpCard: this.helpCard(),
      limitLabel: this.limitLabel,
      tilt01: this.tilt01,
      lockoutUntil: this.host.tick < this.lockoutUntil ? this.lockoutUntil : -1,
      buyIn: this.buyIn(),
      stakeLevel: this.stakeLevel,
      extra: this.extra(),
    };
  }

  /**
   * machines.md: every cabinet prints its declared tag table on a help card. The table is
   * data (`tagTable`); this just renders it in a fixed order so the card never reshuffles.
   */
  helpCard(): HelpCardLine[] {
    const out: HelpCardLine[] = [];
    for (const tag of TAG_ORDER) {
      const rule = this.tagTable[tag];
      if (rule) out.push({ tag, note: rule.note });
    }
    return out;
  }
}

/** Contract `Tag` union order — the one canonical ordering, used wherever tags are listed. */
export const TAG_ORDER: readonly Tag[] = [
  'Magnet', 'Geist', 'Gummi', 'Kleber', 'Spalt', 'Gold',
  'Fluch', 'Feuer', 'Gluecks', 'Schwer', 'Zins', 'Echo',
];

/** GLÜCK bend registry lives on the host; kept here so cabinets share one door to it. */
const bendStore = new WeakMap<object, Set<string>>();
export function bends(host: object): Set<string> {
  let s = bendStore.get(host);
  if (!s) {
    s = new Set<string>();
    bendStore.set(host, s);
  }
  return s;
}
